import { Table } from "antd";
import React, { useMemo } from "react";
import useTableOverflowControls from "./useTableOverflowControls";

const SCROLL_STEP = 18;

const getTitleText = (title) => {
  if (title === null || title === undefined) return "";
  if (typeof title === "string" || typeof title === "number") return String(title);
  if (Array.isArray(title)) return title.map(getTitleText).join(" ").trim();
  if (React.isValidElement(title)) return getTitleText(title.props?.children);
  return "";
};

const normalizeDataLabel = (column, index) => {
  const explicitTitle = getTitleText(column?.title).trim();
  if (explicitTitle) return explicitTitle;

  if (Array.isArray(column?.dataIndex)) {
    return column.dataIndex.join(" ");
  }

  return String(column?.dataIndex || column?.key || `Column ${index + 1}`);
};

const isActionColumn = (column) => {
  const signature = `${column?.key || ""} ${column?.dataIndex || ""} ${getTitleText(column?.title)}`.toLowerCase();
  return signature.includes("action");
};

const BaseTable = (props) => {
  const { columns = [], loader, modkey, containerClassName, rowClassName, ...otherProps } = props;
  void modkey;

  const {
    containerRef,
    hasOverflow,
    showLeftHint,
    showRightHint,
  } = useTableOverflowControls({
    dataSource: otherProps.dataSource,
    columns,
    enabled: true,
    step: SCROLL_STEP,
  });

  const normalizedColumns = useMemo(
    () =>
      columns.map((column, index) => {
        const dataLabel = normalizeDataLabel(column, index);
        const actionColumn = isActionColumn(column);

        return {
          ...column,
          fixed: actionColumn ? (column.fixed || "right") : column.fixed,
          ellipsis: column.ellipsis === false ? false : column.ellipsis || { showTitle: true },
          onCell: (...args) => {
            const original = column.onCell?.(...args) || {};
            const originalClassName = original.className || "";

            return {
              ...original,
              title: original.title || (typeof args?.[0]?.[column?.dataIndex] === "string" ? args[0][column.dataIndex] : undefined),
              "data-label": original["data-label"] || dataLabel,
              className: [
                originalClassName,
                "base-table-cell",
                actionColumn ? "base-table-cell--actions" : "",
              ]
                .filter(Boolean)
                .join(" "),
            };
          },
          onHeaderCell: (...args) => {
            const original = column.onHeaderCell?.(...args) || {};
            return {
              ...original,
              "data-label": dataLabel,
              className: [
                original.className,
                "base-table-header-cell",
                actionColumn ? "base-table-header-cell--actions" : "",
              ]
                .filter(Boolean)
                .join(" "),
            };
          },
        };
      }),
    [columns]
  );

  const mergedScroll = useMemo(
    () => ({
      ...otherProps.scroll,
      x: otherProps.scroll?.x || "max-content",
    }),
    [otherProps.scroll]
  );

  const composedRowClassName = (record, index) => {
    const inheritedClassName =
      typeof rowClassName === "function" ? rowClassName(record, index) : rowClassName;

    return ["base-table-row", inheritedClassName].filter(Boolean).join(" ");
  };

  return (
    <div
      className={ [
        "basetable-container",
        hasOverflow ? "basetable-container--overflowing" : "",
        containerClassName,
      ]
        .filter(Boolean)
        .join(" ") }
    >
      <div className="basetable-shell">
        { hasOverflow ? (
          <>
            <div
              className={ [
                "basetable-edge basetable-edge--left",
                showLeftHint ? "is-visible" : "",
              ]
                .filter(Boolean)
                .join(" ") }
            />
            <div
              className={ [
                "basetable-edge basetable-edge--right",
                showRightHint ? "is-visible" : "",
              ]
                .filter(Boolean)
                .join(" ") }
            />
          </>
        ) : null }

        <div className="basetable-scroll-host" ref={ containerRef }>
          <Table
            rowKey="id"
            size="middle"
            { ...otherProps }
            className={ ["base-table", otherProps.className].filter(Boolean).join(" ") }
            columns={ normalizedColumns }
            rowClassName={ composedRowClassName }
            showSorterTooltip={ false }
            tableLayout={ otherProps.tableLayout || "fixed" }
            loading={ loader }
            scroll={ mergedScroll }
            sticky={ otherProps.sticky || false }
            pagination={ false }
            onRow={ otherProps.onRow }
          />
        </div>
      </div>
    </div>
  );
};

export default BaseTable;
